import {
  Box,
  Flex,
  Heading,
  Button,
  Card,
  CardBody,
  useColorModeValue,
  Icon,
  Stack
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ItemDataSuggestionModel } from 'models';
import ItemCard from '../ItemCard/ItemCard';
import { MdArrowForward, MdCheck, MdClose } from 'react-icons/md';
import { NavigationService } from 'services';

export interface ItemDataSuggestionCardProps {
  suggestion: ItemDataSuggestionModel;
  onApprove?: (suggestionId: string) => void;
  onReject?: (suggestionId: string) => void;
  isLoading?: boolean;
}

export default function ItemDataSuggestionCard({
  suggestion,
  onApprove,
  onReject,
  isLoading = false
}: ItemDataSuggestionCardProps) {
  const navigate = useNavigate();
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleItemClick = (id: string, name: string) => {
    if (!id) {
      return;
    }
    navigate(NavigationService.getItemDetailsPath(id, name));
  };

  const handleApprove = () => {
    onApprove?.(suggestion.id);
  };

  const handleReject = () => {
    onReject?.(suggestion.id);
  };

  const updatedItem = suggestion.getUpdatedItem();

  return (
    <Card variant="outline" borderColor={borderColor}>
      <CardBody>
        <Stack spacing={4}>
          <Flex direction={{ base: 'column', lg: 'row' }} gap={4} alignItems="flex-start">
            <Box flex={1} width="100%">
              <Heading size="xs" mb={2} color="gray.600">
                Current
              </Heading>
              <ItemCard
                item={suggestion.item}
                size="md"
                showMetadata={true}
                defaultMetadataExpanded={true}
                clickOnImage={true}
                clickOnName={true}
                onClick={() => handleItemClick(suggestion.item.id, suggestion.item.getDisplayName())}
              />
            </Box>

            <Flex alignItems="center" justifyContent="center" minWidth="40px">
              <Icon as={MdArrowForward} w={6} h={6} color="gray.400" />
            </Flex>

            <Box flex={1} width="100%">
              <Heading size="xs" mb={2} color="blue.600">
                Proposed
              </Heading>
              <ItemCard
                item={updatedItem}
                size="md"
                showMetadata={true}
                defaultMetadataExpanded={true}
                clickOnImage={true}
                clickOnName={true}
                onClick={() => handleItemClick(updatedItem.id, updatedItem.getDisplayName())}
              />
            </Box>
          </Flex>

          {suggestion.isPending() && (
            <Flex gap={2} justifyContent="flex-end">
              <Button
                leftIcon={<MdClose />}
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={handleReject}
                isDisabled={isLoading}
              >
                Reject
              </Button>
              <Button
                leftIcon={<MdCheck />}
                size="sm"
                colorScheme="green"
                onClick={handleApprove}
                isDisabled={isLoading}
              >
                Approve
              </Button>
            </Flex>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}
